import { ApplicationRef, EnvironmentInjector, Injectable, createComponent, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { ToastHostComponent } from './toast-host.component';
import type {
  ToastComponentOptions,
  ToastId,
  ToastOptions,
  ToastPosition,
  ToastRecord
} from './toast.model';
import { ToastStore } from './toast.store';

function createId(): string {
  // short, unique-enough id for UI toasts
  return `${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

@Injectable({ providedIn: 'root' })
export class ToastService {
  private readonly _store = inject(ToastStore);
  private readonly _appRef = inject(ApplicationRef);
  private readonly _envInjector = inject(EnvironmentInjector);
  private readonly _doc = inject(DOCUMENT);

  private _hostAttached = false;
  private _hostElement?: HTMLElement;
  private _overlayObserver?: MutationObserver;

  private _defaultDurationMs = 4000;
  private _defaultPosition: ToastPosition = 'bottom-right';
  private _defaultMaxVisible = 3;

  /**
   * Call once (optional) to configure defaults.
   */
  public configure(options: {
    durationMs?: number;
    position?: ToastPosition;
    maxVisible?: number;
  }): void {
    if (typeof options.durationMs === 'number') this._defaultDurationMs = options.durationMs;
    if (options.position) this._defaultPosition = options.position;
    if (typeof options.maxVisible === 'number') {
      this._defaultMaxVisible = options.maxVisible;
      this._store.setMaxVisible(options.maxVisible);
    }
  }

  public open(options: ToastOptions): ToastId {
    this._ensureHost();

    const id = options.id ?? createId();
    const durationMs = options.durationMs ?? this._defaultDurationMs;
    const record: ToastRecord = {
      id,
      createdAt: Date.now(),
      variant: options.variant ?? 'default',
      position: options.position ?? this._defaultPosition,
      durationMs,
      dismissible: options.dismissible ?? true,
      content: { kind: 'text', title: options.title, description: options.description },
      action: options.action,
      cancel: options.cancel,
    };

    this._store.add(record);
    this._armTimer(record);
    return id;
  }

  public custom<TComponent>(options: ToastComponentOptions<TComponent>): ToastId {
    this._ensureHost();

    const id = options.id ?? createId();
    const durationMs = options.durationMs ?? this._defaultDurationMs;
    const record: ToastRecord = {
      id,
      createdAt: Date.now(),
      variant: options.variant ?? 'default',
      position: options.position ?? this._defaultPosition,
      durationMs,
      dismissible: options.dismissible ?? true,
      content: {
        kind: 'component',
        component: options.component,
        componentInputs: options.componentInputs as Record<string, unknown> | undefined,
      },
    };

    this._store.add(record);
    this._armTimer(record);
    return id;
  }

  public success(title: string, options: Omit<ToastOptions, 'title' | 'variant'> = {}): ToastId {
    return this.open({ ...options, title, variant: 'success' });
  }

  public info(title: string, options: Omit<ToastOptions, 'title' | 'variant'> = {}): ToastId {
    return this.open({ ...options, title, variant: 'info' });
  }

  public warning(title: string, options: Omit<ToastOptions, 'title' | 'variant'> = {}): ToastId {
    return this.open({ ...options, title, variant: 'warning' });
  }

  public error(title: string, options: Omit<ToastOptions, 'title' | 'variant'> = {}): ToastId {
    return this.open({ ...options, title, variant: 'error' });
  }

  public dismiss(id: ToastId): void {
    this._store.dismiss(id);
  }

  public dismissAll(): void {
    this._store.dismissAll();
  }

  private _ensureHost(): void {
    if (this._hostAttached) {
      // The host can be removed when a Sheet closes if it was mounted into it.
      // Ensure it is still connected and mounted in the best available container.
      this._mountHostInBestContainer();
      return;
    }

    // Ensure store has configured maxVisible
    this._store.setMaxVisible(this._defaultMaxVisible);

    const hostRef = createComponent(ToastHostComponent, {
      environmentInjector: this._envInjector,
    });
    hostRef.instance.defaultPosition = this._defaultPosition;

    this._appRef.attachView(hostRef.hostView);
    this._hostElement = hostRef.location.nativeElement as HTMLElement;

    // Keep an observer alive so we can react to:
    // - Sheet open/close (data-state attribute changes)
    // - CDK overlay container creation/removal
    // - host being removed from the DOM
    this._ensureOverlayObserver();

    // Mount into the best available container right now.
    this._mountHostInBestContainer();

    this._hostAttached = true;
  }

  private _ensureOverlayObserver(): void {
    if (this._overlayObserver) return;
    if (!this._doc.body) return;

    this._overlayObserver = new MutationObserver(() => this._mountHostInBestContainer());
    this._overlayObserver.observe(this._doc.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-state'],
    });
  }

  private _mountHostInBestContainer(): void {
    const host = this._hostElement;
    if (!host) return;

    // If the host was removed from the DOM, remount it.
    // (e.g. when a sheet closes and destroys its content).
    if (!host.isConnected) {
      // Nothing to do here; we will re-append below to the best container.
    }

    // 1) If a Sheet is currently open, mount inside it so the toast is guaranteed
    // to appear above the sheet content/overlay.
    const openSheets = this._doc.querySelectorAll('[data-slot="sheet-content"][data-state="open"]');
    const lastOpenSheet = openSheets.length
      ? (openSheets.item(openSheets.length - 1) as Element)
      : null;
    if (lastOpenSheet instanceof HTMLElement) {
      if (host.parentElement !== lastOpenSheet) {
        lastOpenSheet.appendChild(host);
      }
      return;
    }

    // 2) Otherwise prefer mounting inside the CDK overlay container (Dialogs, Menus, ...),
    // so toasts are still above most overlay content.
    const overlay = this._doc.querySelector('.cdk-overlay-container');
    if (overlay instanceof HTMLElement) {
      if (host.parentElement !== overlay) {
        overlay.appendChild(host);
      }
      return;
    }

    // Fallback: mount into dedicated toast root or body.
    const toastRoot = this._doc.getElementById('cc-toast-root');
    const fallbackTarget = (toastRoot ?? this._doc.body) as HTMLElement | null;
    if (fallbackTarget && host.parentElement !== fallbackTarget) {
      fallbackTarget.appendChild(host);
    }
  }

  private _armTimer(record: ToastRecord): void {
    if (!Number.isFinite(record.durationMs)) return;
    if (record.durationMs <= 0) return;
    // best-effort: if user interacts, they can keep it open by preventDefault in action
    window.setTimeout(() => this.dismiss(record.id), record.durationMs);
  }
}

